import { useEffect, type RefObject } from 'react';

/**
 * Agrandir une photo a deux doigts.
 *
 * WebKit sait le faire seul sur une page ordinaire, mais pas ici : la
 * balise `viewport` interdit le zoom de la page entiere, sans quoi le
 * moindre pincement decalerait toute l'application — barre d'onglets et
 * marges d'encoche comprises. Ce qu'on retire a la page, on le rend donc
 * a la photo, et a elle seule.
 *
 * Rien ne passe par React pendant le geste : une position d'etat, ce
 * serait un rendu de l'arbre a chaque image pour deplacer un seul noeud.
 * On ecrit sur le style, dans une image d'animation, comme pour le
 * tirage vers le bas.
 */

/** Au-dela, on ne lit plus une photo, on lit des pixels. */
const MAX = 4;
/** Ce que double-taper amene, et ce qu'il quitte. */
const DOUBLE = 2.5;
/** Deux appuis plus espaces que cela sont deux appuis distincts. */
const DOUBLE_MS = 300;
/** Duree du retour a la taille d'origine, alignee sur la feuille de style. */
const RETOUR_MS = 220;

interface Point {
  x: number;
  y: number;
}

/** Distance entre deux doigts. */
function ecart(a: Touch, b: Touch): number {
  return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
}

/** Milieu entre deux doigts, relatif au centre de la zone. */
function milieu(a: Touch, b: Touch, zone: DOMRect): Point {
  return {
    x: (a.clientX + b.clientX) / 2 - (zone.left + zone.width / 2),
    y: (a.clientY + b.clientY) / 2 - (zone.top + zone.height / 2),
  };
}

export function usePinchZoom(zone: RefObject<HTMLElement>, image: RefObject<HTMLElement>): void {
  useEffect(() => {
    const el = zone.current;
    if (!el) return;

    let k = 1;
    let t: Point = { x: 0, y: 0 };
    /* L'etat au debut du geste : tout se calcule par rapport a lui. */
    let k0 = 1;
    let t0: Point = { x: 0, y: 0 };
    let d0 = 0;
    let m0: Point = { x: 0, y: 0 };
    let doigts = 0;
    let dernierAppui = 0;
    /*
      Un geste a eu lieu : le clic qui suit ne doit pas refermer.

      Remis a faux a chaque contact neuf, et pas seulement quand le clic
      arrive : iOS n'en emet pas toujours apres un multi-touche, et le
      drapeau reste alors leve — c'est l'appui *suivant*, celui qui
      voulait fermer, qui aurait ete avale.
    */
    let bouge = false;
    let img = 0;

    function poser() {
      if (img) return;
      img = requestAnimationFrame(() => {
        img = 0;
        const noeud = image.current;
        if (!noeud) return;
        noeud.style.transform =
          k === 1 && !t.x && !t.y ? '' : `translate3d(${t.x}px, ${t.y}px, 0) scale(${k})`;
      });
    }

    /**
     * La photo ne quitte pas le cadre.
     *
     * On la retient des que son bord atteint celui de l'ecran : plus loin,
     * on decouvrirait du fond la ou il y a de l'image, et l'ecran parait
     * casse. Mesure contre le cadre visible et non contre la boite de la
     * photo — celle-ci est plus etroite que l'ecran des que la photo est
     * haute, et s'y fier laissait reapparaitre du noir sur les cotes.
     *
     * Tant que la photo agrandie tient dans le cadre, elle n'a nulle part
     * ou aller : la borne vaut zero et elle reste centree.
     */
    function retenir() {
      const noeud = image.current;
      if (!noeud || !el) return;
      const maxX = Math.max(0, (noeud.offsetWidth * k - el.clientWidth) / 2);
      const maxY = Math.max(0, (noeud.offsetHeight * k - el.clientHeight) / 2);
      t = {
        x: Math.min(maxX, Math.max(-maxX, t.x)),
        y: Math.min(maxY, Math.max(-maxY, t.y)),
      };
    }

    /** Retour anime a la taille d'origine, ou simple recadrage. */
    function reposer(anime: boolean) {
      const noeud = image.current;
      if (!noeud) return;
      if (anime) {
        noeud.style.transition = `transform ${RETOUR_MS}ms ease`;
        window.setTimeout(() => {
          if (image.current) image.current.style.transition = '';
        }, RETOUR_MS);
      }
      retenir();
      poser();
    }

    /** Agrandit ou revient, en gardant fixe le point vise. */
    function versEchelle(cible: number, vise: Point) {
      const c = { x: (vise.x - t.x) / k, y: (vise.y - t.y) / k };
      k = cible;
      t = { x: vise.x - c.x * k, y: vise.y - c.y * k };
      reposer(true);
    }

    function onStart(event: TouchEvent) {
      doigts = event.touches.length;
      const zoneRect = el?.getBoundingClientRect();
      if (!zoneRect) return;

      if (doigts === 2) {
        const [a, b] = [event.touches[0], event.touches[1]];
        d0 = ecart(a, b);
        m0 = milieu(a, b, zoneRect);
        k0 = k;
        t0 = t;
        bouge = true;
        // Un pincement annule le compte a rebours du double appui.
        dernierAppui = 0;
        return;
      }

      if (doigts !== 1) return;
      bouge = false;
      const doigt = event.touches[0];
      m0 = {
        x: doigt.clientX - (zoneRect.left + zoneRect.width / 2),
        y: doigt.clientY - (zoneRect.top + zoneRect.height / 2),
      };
      t0 = t;

      /*
        Le double appui, mesure ici plutot que laisse au navigateur :
        WebKit ne l'annonce pas dans une page ou le zoom est interdit.
      */
      const maintenant = event.timeStamp;
      if (dernierAppui && maintenant - dernierAppui < DOUBLE_MS) {
        dernierAppui = 0;
        bouge = true;
        if (k > 1) {
          k = 1;
          t = { x: 0, y: 0 };
          reposer(true);
        } else {
          versEchelle(DOUBLE, m0);
        }
        return;
      }
      dernierAppui = maintenant;
    }

    function onMove(event: TouchEvent) {
      const zoneRect = el?.getBoundingClientRect();
      if (!zoneRect) return;

      if (event.touches.length === 2) {
        event.preventDefault();
        const [a, b] = [event.touches[0], event.touches[1]];
        const d = ecart(a, b);
        if (!d0) return;
        const m = milieu(a, b, zoneRect);
        /*
          On borne des le geste plutot qu'au lacher : depasser puis
          revenir d'un coup donne l'impression que le doigt a decroche.
          Un huitieme au-dela de la borne laisse quand meme sentir qu'on
          pousse contre quelque chose.
        */
        k = Math.min(MAX * 1.125, Math.max(0.5, (k0 * d) / d0));
        t = { x: m.x - ((m0.x - t0.x) * k) / k0, y: m.y - ((m0.y - t0.y) * k) / k0 };
        bouge = true;
        poser();
        return;
      }

      // A un doigt, on ne deplace que ce qui deborde.
      if (event.touches.length !== 1 || k <= 1) return;
      event.preventDefault();
      const doigt = event.touches[0];
      const m = {
        x: doigt.clientX - (zoneRect.left + zoneRect.width / 2),
        y: doigt.clientY - (zoneRect.top + zoneRect.height / 2),
      };
      t = { x: t0.x + (m.x - m0.x), y: t0.y + (m.y - m0.y) };
      bouge = true;
      retenir();
      poser();
    }

    function onEnd(event: TouchEvent) {
      if (event.touches.length) {
        /*
          Un doigt reste : le geste continue, mais son origine change. Sans
          ce rebasage, relacher un doigt d'un pincement faisait sauter la
          photo de tout le chemin deja parcouru.
        */
        doigts = event.touches.length;
        const zoneRect = el?.getBoundingClientRect();
        const doigt = event.touches[0];
        if (zoneRect && doigt) {
          m0 = {
            x: doigt.clientX - (zoneRect.left + zoneRect.width / 2),
            y: doigt.clientY - (zoneRect.top + zoneRect.height / 2),
          };
          t0 = t;
        }
        return;
      }
      doigts = 0;

      // Trop petit : la photo reprend sa place. Trop grand : elle revient
      // a la borne. Entre les deux, on garde ce que le doigt a laisse.
      if (k <= 1) {
        k = 1;
        t = { x: 0, y: 0 };
        reposer(true);
      } else if (k > MAX) {
        versEchelle(MAX, { x: 0, y: 0 });
      } else {
        reposer(false);
      }
    }

    /*
      Le clic de fin de geste ne doit pas refermer la visionneuse : le
      voile de fond est un bouton, et un pincement s'y termine par un clic
      comme n'importe quel appui. On l'intercepte a la capture, avant
      qu'il n'atteigne le bouton.
    */
    function onClick(event: MouseEvent) {
      if (!bouge) return;
      bouge = false;
      event.stopPropagation();
      event.preventDefault();
    }

    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: false });
    el.addEventListener('touchend', onEnd, { passive: true });
    el.addEventListener('touchcancel', onEnd, { passive: true });
    el.addEventListener('click', onClick, true);

    return () => {
      if (img) cancelAnimationFrame(img);
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', onEnd);
      el.removeEventListener('click', onClick, true);
    };
  }, [zone, image]);
}
